/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * ERUE Festival Suite — App.tsx (DROP 40F CLEAN)
 * © Digitrax Finteca Consulting
 */

import React, { useState, useEffect } from 'react';
import { GlobalToastContainer } from './components/ui/GlobalToast';
import { PwaInstallPrompt }            from './components/PwaInstallPrompt';
import NewsBar                          from './components/shared/NewsBar';
import { UserPasswordSettingsModal }    from './components/UserPasswordSettingsModal';
import { AdminUserManagementModal }     from './components/AdminUserManagementModal';
import LoginPortalModal                 from './components/LoginPortalModal';
import AccessControlGuard               from './components/AccessControlGuard';
import { Footer }                       from './components/festival/Footer';
import MainNavbar                       from './components/navigation/MainNavbar';
import { getStoredUser }               from './utils/auth';
import { UserAccount, PortalTab, hasPermission, resolvePermissions, ROLE_PERMISSIONS } from './types';

/* ── Festival Pages ─────────────────────────────────────── */
import PublicHomePage          from './components/festival/PublicHomePage';
import { FestivalProgramme }   from './components/festival/FestivalProgramme';
import { FestivalCalendar }     from './components/festival/FestivalCalendar';
import FestivalProgrammeBuilder from './components/festival/FestivalProgrammeBuilder';

import MediaGallery            from './components/festival/MediaGallery';
import LiveStreamHub           from './components/festival/LiveStreamHub';

/* ── Kingdom Pages ──────────────────────────────────────── */
import OgieneniPortal          from './components/festival/OgieneniPortal';
import DiagnitariesBook        from './components/admin/DiagnitariesBook';
import { CommunitiesShowcase } from './components/festival/CommunitiesShowcase';
import BoardOfTrusteesPortal    from './components/governance/BoardOfTrusteesPortal';
import CWCPortal               from './components/governance/CWCPortal';
import LOCPortal               from './components/governance/LOCPortal';
import ManagementCommitteePanel from './components/governance/ManagementCommitteePanel';

/* ── Participate Pages ──────────────────────────────────── */
import EventRegistrationHub    from './components/festival/EventRegistrationHub';
import ContestantVotingBooth   from './components/festival/ContestantVotingBooth';
import RaffleEngine             from './components/festival/RaffleEngine';
import FundRaisingPage          from './components/fundraising/FundRaisingPage';
import VendorRegistrationPage   from './components/vendor/VendorRegistrationPage';
import PaystackStore            from './components/festival/PaystackStore';

/* ── Information Pages ──────────────────────────────────── */
import { ErueNewsPortal }      from './components/festival/ErueNewsPortal';
import UzairueKingdomPortal      from './components/festival/UzairueKingdomPortal';
import CollaborationHub         from './components/collaboration/CollaborationHub';
import MetaDistributionPanel     from './components/admin/MetaDistributionPanel';
import { VisitorGuide }        from './components/festival/VisitorGuide';
import { DiasporaPortal }      from './components/festival/DiasporaPortal';
import { SponsorshipSupport }  from './components/festival/SponsorshipSupport';
import PublicContactForm       from './components/festival/PublicContactForm';
import { CulturalHeritage }    from './components/festival/CulturalHeritage';
import CommunityDetailPage     from './components/festival/CommunityDetailPage';

/* ── Staff / Admin Pages ────────────────────────────────── */
import ContactsDirectory        from './components/admin/ContactsDirectory';
import FinancialHub             from './components/finance/FinancialHub';
import SuperAdminDashboard     from './components/admin/SuperAdminDashboard';
import PlatformAdminConsole    from './components/admin/PlatformAdminConsole';
import GovernanceAdmin         from './components/governance/GovernanceAdmin';
import VillageAdminConsole     from './components/community/VillageAdminConsole';
import CommunitySubPortal      from './components/community/CommunitySubPortal';
import CommunityAdminPortal    from './components/community/CommunityAdminPortal';
import CommunityManager        from './components/admin/CommunityManager';
import BudgetManager           from './components/finance/BudgetManager';
import ReportsModule           from './components/admin/ReportsModule';
import VendorAdminPanel        from './components/admin/VendorAdminPanel';
import FundRaisingAdminPanel   from './components/admin/FundRaisingAdminPanel';
import PaystackConfigPanel     from './components/admin/PaystackConfigPanel';
import { FinancialManagementModule } from './components/admin/FinancialManagementModule';
import { CommunicationPortal } from './components/communication/CommunicationPortal';
import { ResourcesInvestmentHub } from './components/festival/ResourcesInvestmentHub';
import LegalPages               from './components/legal/LegalPages';
import type { LegalDoc }         from './components/legal/LegalPages';
import LegalFooter              from './components/legal/LegalFooter';
import { VisitorGuideAdmin }     from './components/admin/VisitorGuideAdmin';
import { DeepLinksPanel }        from './components/shared/DeepLinksPanel';

/* ─────────────────────────────────────────────────────────
   TABS THAT REQUIRE STAFF LOGIN
───────────────────────────────────────────────────────── */
const RESTRICTED_TABS: ReadonlySet<string> = new Set([
  'document-manager',
  'financial-manager',
  'accounting',
  'reports',
  'raffle-admin',
  'vendor-admin',
  'publishing-suite',
  'paystack-config',
  'community-admin',
  'fundraising-admin',
  'village-admin',
  'governance-admin',
  'resources-investment',
  'communication-portal',
  'financial-hub',
  'contacts-directory',
  'collaboration',
  'collaboration-admin',
]);

/* ─────────────────────────────────────────────────────────
   REUSABLE ACCESS-DENIED BLOCK
───────────────────────────────────────────────────────── */
function AccessDenied({
  icon, title, message, onLogin, needLogin,
}: {
  icon: string; title: string; message: string;
  onLogin: () => void; needLogin: boolean;
}) {
  return (
    <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#070D0A' }}>
      <div style={{ textAlign: 'center', padding: '40px 32px', background: '#121E16', border: `1px solid ${needLogin ? '#C9A84C33' : '#ef444433'}`, borderRadius: 16, maxWidth: 400 }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>{icon}</div>
        <h2 style={{ color: needLogin ? '#C9A84C' : '#ef4444', fontFamily: 'Cinzel,serif', marginBottom: 8, fontSize: 18 }}>{title}</h2>
        <p style={{ color: '#9CA38F', fontSize: 13, lineHeight: 1.6, marginBottom: needLogin ? 16 : 0 }}>{message}</p>
        {needLogin && (
          <button onClick={onLogin} style={{ padding: '10px 24px', background: '#0F5132', border: '1px solid #C9A84C44', borderRadius: 6, color: '#C9A84C', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            Staff Login
          </button>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   APP COMPONENT
═══════════════════════════════════════════════════════════ */
export default function App() {
  const [activePortalTab,  setActivePortalTab]  = useState<PortalTab>('festival-home');
  const [currentUser,      setCurrentUser]      = useState<UserAccount | null>(() => {
    // Try ghost auth first, then fall back to JWT-stored user
    const ghost = getStoredUser();
    if (ghost) return ghost;
    try {
      const stored = localStorage.getItem('erue_auth_data');
      if (stored) return JSON.parse(stored);
    } catch {}
    return null;
  });
  const [isLoginPortalOpen, setIsLoginPortalOpen] = useState(false);
  const [loginCanDismiss,  setLoginCanDismiss]  = useState(true);
  const [isPasswordSettingsOpen, setIsPasswordSettingsOpen] = useState(false);
  const [isAdminModalOpen, setIsAdminModalOpen] = useState(false);
  const [showPlatformAdmin, setShowPlatformAdmin] = useState(false);
  const [showSuperAdmin,    setShowSuperAdmin]   = useState(false);
  const [selectedCommunitySlug, setSelectedCommunitySlug] = useState<string | undefined>(undefined);
  const [selectedVillagePortal, setSelectedVillagePortal] = useState<{ id: string; name: string } | null>(null);
  const [legalDoc, setLegalDoc] = useState<LegalDoc | null>(null);
  const [showCommunityAdmin, setShowCommunityAdmin] = useState(false);
  const [firstLogin, setFirstLogin] = useState(false);

  // ── Navigation handler ───────────────────────────────────
  const handleExtendedNav = React.useCallback((view: string) => {
    if (view === 'platform-admin') {
      setShowPlatformAdmin(true);
    } else {
      setActivePortalTab(view as PortalTab);
    }
  }, []);

  // ── Logout ───────────────────────────────────────────────
  const handleLogout = React.useCallback(() => {
    setCurrentUser(null);
    setActivePortalTab('festival-home');
    setShowPlatformAdmin(false);
    localStorage.removeItem('erue_auth_token');
    localStorage.removeItem('erue_auth_data');
    sessionStorage.removeItem('erue_auth_token');
  }, []);

  // ── Redirect unauthenticated users from restricted tabs ──
  useEffect(() => {
    if (!currentUser && RESTRICTED_TABS.has(activePortalTab)) {
      setActivePortalTab('festival-home');
      setIsLoginPortalOpen(true);
      setLoginCanDismiss(true);
    }
  }, [currentUser, activePortalTab]);

  // ── Scroll to top on tab change ──────────────────────────
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activePortalTab]);

  // ── Token helper ─────────────────────────────────────────
  const getToken = () =>
    localStorage.getItem('erue_auth_token') || sessionStorage.getItem('erue_auth_token') || '';

  // ── Role helper ───────────────────────────────────────────
  const userRole = (currentUser?.systemRole || currentUser?.role || '').toUpperCase();

  // ── Login modal helper ───────────────────────────────────
  const openLogin = () => { setIsLoginPortalOpen(true); setLoginCanDismiss(true); };

  // ── Full-screen overrides ────────────────────────────────
  if (showSuperAdmin) {
    return (
      <SuperAdminDashboard
        currentUser={currentUser}
        onLogout={() => { setShowSuperAdmin(false); setCurrentUser(null); }}
        onNavigate={(tab) => { setShowSuperAdmin(false); setActivePortalTab(tab); }}
      />
    );
  }

  if (selectedVillagePortal && !showCommunityAdmin) {
    return (
      <CommunitySubPortal
        villageId={selectedVillagePortal.id}
        villageName={selectedVillagePortal.name}
        onBack={() => setSelectedVillagePortal(null)}
        onAdminAccess={() => setShowCommunityAdmin(true)}
        isAdmin={!!(currentUser && (
          currentUser.systemRole === 'PLATFORM_ADMIN' ||
          currentUser.role === 'admin' ||
          currentUser.email === 'erueuzairue@gmail.com'
        ))}
      />
    );
  }

  if (selectedVillagePortal && showCommunityAdmin) {
    return (
      <CommunityAdminPortal
        villageId={selectedVillagePortal.id}
        villageName={selectedVillagePortal.name}
        authToken={getToken()}
        onBack={() => { setShowCommunityAdmin(false); setSelectedVillagePortal(null); }}
        onPreview={() => setShowCommunityAdmin(false)}
      />
    );
  }

  // ── RBAC shorthand helpers ───────────────────────────────
  const isAdmin        = ['PLATFORM_ADMIN'].includes(userRole) || currentUser?.email === 'erueuzairue@gmail.com';

  // ── Governance role helpers ──────────────────────────────────────────────
  const userPerms   = currentUser?.permissions || [];
  const isBoardMember = isAdmin || ['PLATFORM_ADMIN','BOARD_CHAIR','BOARD_TRUSTEE'].includes(userRole) ||
                        hasPermission(userRole, 'view_board', userPerms);
  const isCWCMember   = isAdmin || ['PLATFORM_ADMIN','CWC_TREASURER','CWC_SECRETARY','CWC_MEMBER'].includes(userRole) ||
                        hasPermission(userRole, 'view_cwc', userPerms);
  const isLOCMember   = isAdmin || ['PLATFORM_ADMIN','LOC_CHAIR','LOC_MEMBER'].includes(userRole) ||
                        hasPermission(userRole, 'view_loc', userPerms);
  const canViewBudget = hasPermission(userRole, 'budget_view', userPerms);
  const canEditBudget = hasPermission(userRole, 'budget_edit', userPerms);
  const canApproveBudget = hasPermission(userRole, 'board_approve', userPerms);
  const canExportReports = hasPermission(userRole, 'reports_export', userPerms);
  const isAccountant   = ['PLATFORM_ADMIN','ACCOUNTANT','FESTIVAL_COORDINATOR'].includes(userRole) || (currentUser?.permissions || []).includes('accounting');
  const isRaffleAdmin  = ['PLATFORM_ADMIN','ACCOUNTANT','RAFFLE_MANAGER'].includes(userRole);
  const isVendorAdmin  = ['PLATFORM_ADMIN','FESTIVAL_COORDINATOR'].includes(userRole);
  const isPublisher    = ['PLATFORM_ADMIN','SECRETARY','FESTIVAL_COORDINATOR'].includes(userRole);
  const isDocManager   = ['PLATFORM_ADMIN','SECRETARY','FESTIVAL_COORDINATOR','MEDIA_MANAGER'].includes(userRole)
                         || (currentUser?.permissions || []).some(p => ['documents','news_publishing'].includes(p));

  /* ══════════════════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════════════════ */
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#070D0A', color: '#EDE8DC', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>

      {/* ── Global Navigation ─────────────────────────────── */}
      <MainNavbar
        currentUser={currentUser}
        activeTab={activePortalTab}
        setActiveTab={handleExtendedNav}
        onLoginClick={openLogin}
        onLogout={handleLogout}
      />

      {/* ── Breaking News Ticker ───────────────────────────── */}
      <NewsBar />

      {/* ── Main Content ──────────────────────────────────── */}
      <main style={{ flex: 1, background: '#070D0A' }} className="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* ════════════════════════════════════════════════════
            FESTIVAL TAB GROUP
        ════════════════════════════════════════════════════ */}
        {(activePortalTab === 'festival-home' || activePortalTab === '') && (
          <PublicHomePage
            onNavigate={handleExtendedNav}
            currentUser={currentUser}
            onLoginClick={openLogin}
          />
        )}

        {activePortalTab === 'programme' && (
          <FestivalProgramme />
        )}

        {activePortalTab === 'schedule' && (
          <FestivalProgrammeBuilder
            adminMode={isAdmin || currentUser?.role === 'admin'}
            currentUser={currentUser}
          />
        )}

        {activePortalTab === 'calendar' && <FestivalCalendar />}

        {activePortalTab === 'gallery' && (
          <MediaGallery currentUser={currentUser} />
        )}

        {activePortalTab === 'live-stream' && (
          <LiveStreamHub currentUser={currentUser} />
        )}

        {/* ════════════════════════════════════════════════════
            KINGDOM TAB GROUP
        ════════════════════════════════════════════════════ */}
        {activePortalTab === 'ogieneni' && (
          <OgieneniPortal currentUser={currentUser} />
        )}
        {activePortalTab === 'uzairue-portal' && (
          <UzairueKingdomPortal currentUser={currentUser} />
        )}


        {activePortalTab === 'dignitaries' && (
          <DiagnitariesBook
            isAdmin={isAdmin || currentUser?.role === 'admin'}
            currentUser={currentUser}
          />
        )}

        {activePortalTab === 'communities' && (
          <CommunitiesShowcase
            setActiveTab={setActivePortalTab}
            onSelectVillage={(village: any) => {
              setSelectedVillagePortal({ id: village.id, name: village.name });
              if (village._adminEdit) setShowCommunityAdmin(true);
            }}
            currentUser={currentUser}
          />
        )}

        {activePortalTab === 'community-detail' && (
          <CommunityDetailPage
            slug={selectedCommunitySlug}
            setActiveTab={setActivePortalTab}
          />
        )}

        {activePortalTab === 'board-of-trustees' && (
          !currentUser ? (
            <AccessDenied icon="🏛️" title="Board of Trustees Portal" needLogin={true}
              message="Please log in to access the Board of Trustees portal."
              onLogin={openLogin} />
          ) : !isBoardMember ? (
            <AccessDenied icon="⛔" title="Access Restricted" needLogin={false}
              message="The Board of Trustees portal is restricted to Board members and Platform Administrators."
              onLogin={openLogin} />
          ) : (
            <BoardOfTrusteesPortal currentUser={currentUser} />
          )
        )}

        {activePortalTab === 'cwc' && (
          !currentUser ? (
            <AccessDenied icon="⚙️" title="CWC Portal" needLogin={true}
              message="Please log in to access the Central Working Committee portal."
              onLogin={openLogin} />
          ) : !isCWCMember ? (
            <AccessDenied icon="⛔" title="Access Restricted" needLogin={false}
              message="The CWC portal is restricted to CWC members and Platform Administrators."
              onLogin={openLogin} />
          ) : (
            <CWCPortal currentUser={currentUser} />
          )
        )}

        {activePortalTab === 'loc' && (
          !currentUser ? (
            <AccessDenied icon="📋" title="LOC Portal" needLogin={true}
              message="Please log in to access the LOC portal."
              onLogin={openLogin} />
          ) : !isLOCMember ? (
            <AccessDenied icon="⛔" title="Access Restricted" needLogin={false}
              message="The LOC portal is restricted to LOC members and Platform Administrators."
              onLogin={openLogin} />
          ) : (
            <LOCPortal currentUser={currentUser} />
          )
        )}

        {activePortalTab === 'management-committee' && (
          <ManagementCommitteePanel />
        )}

        {activePortalTab === 'heritage' && <CulturalHeritage />}

        {/* ════════════════════════════════════════════════════
            PARTICIPATE TAB GROUP
        ════════════════════════════════════════════════════ */}
        {activePortalTab === 'registrations' && (
          <EventRegistrationHub currentUser={currentUser} />
        )}

        {activePortalTab === 'voting' && (
          <ContestantVotingBooth currentUser={currentUser} />
        )}

        {activePortalTab === 'raffle' && (
          <RaffleEngine currentUser={currentUser} />
        )}

        {activePortalTab === 'donate' && (
          <FundRaisingPage onNavigate={setActivePortalTab} />
        )}

        {activePortalTab === 'vendors' && <VendorRegistrationPage />}

        {activePortalTab === 'store' && (
          <PaystackStore currentUser={currentUser} />
        )}

        {/* ════════════════════════════════════════════════════
            INFORMATION TAB GROUP
        ════════════════════════════════════════════════════ */}
        {activePortalTab === 'news' && (
          <ErueNewsPortal currentUser={currentUser} adminMode={isAdmin} />
        )}

        {activePortalTab === 'visitor-guide' && <VisitorGuideAdmin currentUser={currentUser} />}

        {activePortalTab === 'diaspora-portal' && (
          <DiasporaPortal currentUser={currentUser} />
        )}

        {activePortalTab === 'sponsorship' && <SponsorshipSupport />}

        {activePortalTab === 'contact' && <PublicContactForm />}

        {/* ════════════════════════════════════════════════════
            STAFF PORTAL — PUBLISHING & DOCUMENTS
        ════════════════════════════════════════════════════ */}
        {activePortalTab === 'document-manager' && (
          !currentUser ? (
            <AccessDenied icon="🔐" title="Document Manager" needLogin={true}
              message="Staff login required to access the Document Manager."
              onLogin={openLogin} />
          ) : !isDocManager ? (
            <AccessDenied icon="⛔" title="Access Denied" needLogin={false}
              message="You do not have permission to access the Document Manager. Contact the Platform Administrator."
              onLogin={openLogin} />
          ) : (
            <ErueNewsPortal currentUser={currentUser} adminMode={isAdmin} />
          )
        )}

        {activePortalTab === 'publishing-suite' && (
          !currentUser ? (
            <AccessDenied icon="📰" title="Publishing Suite" needLogin={true}
              message="Staff login required to access the Publishing Suite."
              onLogin={openLogin} />
          ) : !isPublisher ? (
            <AccessDenied icon="⛔" title="Access Denied" needLogin={false}
              message="Insufficient permissions to access the Publishing Suite."
              onLogin={openLogin} />
          ) : (
            <ErueNewsPortal currentUser={currentUser} adminMode={true} />
          )
        )}

        {/* ════════════════════════════════════════════════════
            STAFF PORTAL — FINANCE
        ════════════════════════════════════════════════════ */}
        {activePortalTab === 'accounting' && (
          !currentUser ? (
            <AccessDenied icon="💰" title="Budget Manager" needLogin={true}
              message="Staff login required to access the Budget Manager."
              onLogin={openLogin} />
          ) : !isAccountant ? (
            <AccessDenied icon="⛔" title="Access Denied" needLogin={false}
              message="The Budget Manager is restricted to Accountants, Festival Coordinators, and Platform Administrators."
              onLogin={openLogin} />
          ) : (
            <BudgetManager currentUser={currentUser} />
          )
        )}

        {activePortalTab === 'reports' && (
          <ReportsModule currentUser={currentUser} />
        )}

        {activePortalTab === 'financial-manager' && (
          <AccessControlGuard
            currentUser={currentUser || undefined}
            onLoginClick={openLogin}
            requiredRole="admin"
            restrictedAreaId="financial-manager"
            title="Financial Management Access Restricted"
            description="The Financial Management Module is restricted to authorized Administrators and financial officers."
          >
            <FinancialManagementModule />
          </AccessControlGuard>
        )}

        {activePortalTab === 'paystack-config' && (
          !currentUser || !isAdmin ? (
            <AccessDenied icon="💳" title="Paystack Configuration" needLogin={!currentUser}
              message={!currentUser ? 'Staff login required.' : 'Platform Admin access required.'}
              onLogin={openLogin} />
          ) : (
            <PaystackConfigPanel />
          )
        )}

        {/* ════════════════════════════════════════════════════
            STAFF PORTAL — COMMUNITY & VILLAGES
        ════════════════════════════════════════════════════ */}
        {activePortalTab === 'village-admin' && (
          <VillageAdminConsole
            currentUser={currentUser}
            onSelectVillage={(village: any) => {
              setSelectedVillagePortal({ id: village.id, name: village.name });
              setShowCommunityAdmin(true);
            }}
          />
        )}

        {activePortalTab === 'community-admin' && (
          !currentUser || !isAdmin ? (
            <AccessDenied icon="🏘️" title="Community Admin" needLogin={!currentUser}
              message={!currentUser ? 'Staff login required.' : 'Insufficient permissions.'}
              onLogin={openLogin} />
          ) : (
            <CommunityManager currentUser={currentUser} />
          )
        )}

        {/* ════════════════════════════════════════════════════
            STAFF PORTAL — GOVERNANCE
        ════════════════════════════════════════════════════ */}
        {activePortalTab === 'governance-admin' && (
          <GovernanceAdmin token={getToken()} />
        )}

        {/* ════════════════════════════════════════════════════
            STAFF PORTAL — VENDORS & FUNDRAISING
        ════════════════════════════════════════════════════ */}
        {activePortalTab === 'vendor-admin' && (
          !currentUser ? (
            <AccessDenied icon="🏪" title="Vendor Admin" needLogin={true}
              message="Staff login required."
              onLogin={openLogin} />
          ) : !isVendorAdmin ? (
            <AccessDenied icon="⛔" title="Access Denied" needLogin={false}
              message="Insufficient permissions to access Vendor Admin."
              onLogin={openLogin} />
          ) : (
            <VendorAdminPanel currentUser={currentUser} />
          )
        )}

        {activePortalTab === 'fundraising-admin' && (
          !currentUser || !isAdmin ? (
            <AccessDenied icon="🤝" title="Fundraising Admin" needLogin={!currentUser}
              message={!currentUser ? 'Staff login required.' : 'Insufficient permissions.'}
              onLogin={openLogin} />
          ) : (
            <FundRaisingAdminPanel currentUser={currentUser} />
          )
        )}

        {/* ════════════════════════════════════════════════════
            STAFF PORTAL — RAFFLE ADMIN
        ════════════════════════════════════════════════════ */}
        {activePortalTab === 'raffle-admin' && (
          !currentUser ? (
            <AccessDenied icon="🎰" title="Raffle Admin" needLogin={true}
              message="Staff login required."
              onLogin={openLogin} />
          ) : !isRaffleAdmin ? (
            <AccessDenied icon="⛔" title="Access Denied" needLogin={false}
              message="Raffle Admin requires RAFFLE_MANAGER or ACCOUNTANT role."
              onLogin={openLogin} />
          ) : (
            <RaffleEngine currentUser={currentUser} adminMode={true} />
          )
        )}

        {/* ════════════════════════════════════════════════════
            STAFF PORTAL — COMMUNICATION & RESOURCES
        ════════════════════════════════════════════════════ */}
        {activePortalTab === 'communication-portal' && (
          <AccessControlGuard
            currentUser={currentUser || undefined}
            onLoginClick={openLogin}
            requiredRole="writer"
            restrictedAreaId="communication-portal"
            title="Kingdom Communication Portal Restricted"
            description="Access to internal kingdom communications is restricted to authorized Secretariat accounts."
          >
            <CommunicationPortal />
          </AccessControlGuard>
        )}

        {activePortalTab === 'financial-hub' && (
          !currentUser ? (
            <AccessDenied icon="💰" title="Financial Hub" needLogin={true}
              message="Staff login required to access the Financial Hub."
              onLogin={openLogin} />
          ) : !isAccountant ? (
            <AccessDenied icon="⛔" title="Access Restricted" needLogin={false}
              message="The Financial Hub is restricted to Accountants and Platform Administrators."
              onLogin={openLogin} />
          ) : (
            <FinancialHub currentUser={currentUser} />
          )
        )}

        {activePortalTab === 'contacts-directory' && (
          !currentUser ? (
            <AccessDenied icon="📋" title="Contacts Directory" needLogin={true}
              message="Staff login required to access the Contacts Directory."
              onLogin={openLogin} />
          ) : (
            <ContactsDirectory currentUser={currentUser} />
          )
        )}

        {activePortalTab === 'resources-investment' && (
          <AccessControlGuard
            currentUser={currentUser || undefined}
            onLoginClick={openLogin}
            requiredRole="writer"
            restrictedAreaId="resources-investment"
            title="Resources & Investment Hub Restricted"
            description="The Kingdom Resources & Investment Hub requires authorized Secretariat credentials."
          >
            <ResourcesInvestmentHub />
          </AccessControlGuard>
        )}

        {/* ── Collaboration Hub ─────────────────────────── */}
        {activePortalTab === 'collaboration' && (
          !currentUser ? (
            <AccessDenied icon="🤝" title="Collaboration Hub" needLogin={true}
              message="Please log in to access the Collaboration Hub."
              onLogin={openLogin} />
          ) : (
            <CollaborationHub currentUser={currentUser} />
          )
        )}

        {activePortalTab === 'collaboration-admin' && (
          !currentUser || !isAdmin ? (
            <AccessDenied icon="🔐" title="Collaboration Admin" needLogin={!currentUser}
              message={!currentUser ? 'Staff login required.' : 'Platform Admin access required.'}
              onLogin={openLogin} />
          ) : (
            <CollaborationHub currentUser={currentUser} adminMode={true} />
          )
        )}

        {/* ── Meta Distribution Panel ──────────────────── */}
        {activePortalTab === 'meta-distribution' && (
          !currentUser || !isAdmin ? (
            <AccessDenied icon="📡" title="Meta Distribution" needLogin={!currentUser}
              message={!currentUser ? 'Staff login required.' : 'Platform Admin access required.'}
              onLogin={openLogin} />
          ) : (
            <MetaDistributionPanel committeeId="admin" />
          )
        )}


      </main>

      {/* ── Global Footer ─────────────────────────────────── */}
      <Footer
        setActiveTab={setActivePortalTab}
        onLoginClick={openLogin}
        currentUser={currentUser}
        onLogout={handleLogout}
      />

      {/* ── PWA Install Prompt ────────────────────────────── */}
      <PwaInstallPrompt />

      {/* ── Modals ────────────────────────────────────────── */}
      <LoginPortalModal
        isOpen={isLoginPortalOpen}
        currentUser={currentUser}
        onLoginSuccess={(user) => {
          setCurrentUser(user);
          setIsLoginPortalOpen(false);
          setLoginCanDismiss(true);
        }}
        onClose={() => { setIsLoginPortalOpen(false); setLoginCanDismiss(true); }}
        canDismiss={loginCanDismiss}
      />

      {currentUser && (
        <UserPasswordSettingsModal
          isOpen={isPasswordSettingsOpen}
          onClose={() => setIsPasswordSettingsOpen(false)}
          currentUser={currentUser}
          onUserUpdated={setCurrentUser}
        />
      )}

      {currentUser && (
        <AdminUserManagementModal
          isOpen={isAdminModalOpen}
          onClose={() => setIsAdminModalOpen(false)}
          currentUser={currentUser}
        />
      )}

      {/* ── Platform Admin Console ────────────────────────── */}
      {showPlatformAdmin && (
        <PlatformAdminConsole
          onClose={() => setShowPlatformAdmin(false)}
          currentUserEmail={currentUser?.email || ''}
        />
      )}

      {/* ── DROP 52A: Legal Pages Modal ─────────────────────── */}
      {legalDoc && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9000,
          background: 'rgba(0,0,0,0.85)',
          overflowY: 'auto',
          display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ flex: 1, maxWidth: 1000, margin: '0 auto', width: '100%', padding: '16px' }}>
            <LegalPages
              initialDoc={legalDoc}
              embedded={false}
              onClose={() => setLegalDoc(null)}
            />
          </div>
        </div>
      )}

      {/* ── DROP 52C: Legal Footer ───────────────────────────── */}
      <LegalFooter onOpenLegal={(doc) => setLegalDoc(doc)} />

      <GlobalToastContainer />
    </div>
  );
}
